import React, { useState, useEffect, useMemo } from 'react';
import { apiService } from '../apiService';
import Header from './Header';
import EventsTimeline from './EventsTimeline';
import TranscriptPanel from './TranscriptPanel';
import FlowServiceAnalyzer from './FlowServiceAnalyzer';
import TagsServiceAnalyzer from './TagsServiceAnalyzer';
import VoiceStreamAnalyzer from './VoiceStreamAnalyzer';
import TranscriptionServiceAnalyzer from './TranscriptionServiceAnalyzer';
import VirtualAssistantConfig from './VirtualAssistantConfig';
import ESSConfig from './ESSConfig';
import SLIAnalyzer from './SLIAnalyzer';

interface TranscriptViewerProps {
  userEmail: string;
  onLogout: () => void;
}

interface SectorData {
  sectors: string[];
  sector_project_mapping: Record<string, string>;
  sector_gke_mapping: Record<string, string>;
}

const TranscriptViewer: React.FC<TranscriptViewerProps> = ({ userEmail, onLogout }) => {
  const [callData, setCallData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState<number | undefined>();
  
  // Search form state
  const [callId, setCallId] = useState(window.localStorage.getItem('lastCallId') || '');
  const [domainId, setDomainId] = useState(window.localStorage.getItem('lastDomainId') || '');
  const [sector, setSector] = useState(window.localStorage.getItem('lastSector') || '');
  const [agentId, setAgentId] = useState('');
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [agent, setAgent] = useState<Record<string, any>>({});
  const [agents, setAgents] = useState<Record<string, Record<string, any>>>({});
  const [availableSectors, setAvailableSectors] = useState<string[]>([]);
  const [sectorMapping, setSectorMapping] = useState<Record<string, string>>({});
  const [gkeMapping, setGkeMapping] = useState<Record<string, string>>({});

  // Virtual Assistant Config Modal state
  const [showVAConfig, setShowVAConfig] = useState(false);
  const [vaConfigLoading, setVAConfigLoading] = useState(false);
  const [vaConfigData, setVAConfigData] = useState<any>(null);

  // ESS Config Modal state
  const [showESSConfig, setShowESSConfig] = useState(false);
  const [essConfigLoading, setESSConfigLoading] = useState(false);
  const [essConfigData, setESSConfigData] = useState<any>(null);

  // Service Analyzer Modal states
  const [showFlowAnalyzer, setShowFlowAnalyzer] = useState(false);
  const [showTranscriptionAnalyzer, setShowTranscriptionAnalyzer] = useState(false);
  const [showTagsAnalyzer, setShowTagsAnalyzer] = useState(false);
  const [showVoiceStreamAnalyzer, setShowVoiceStreamAnalyzer] = useState(false);

  const [showSLI, setShowSLI] = useState(false);

  const missingVSSub = useMemo(() => {
    if (!callData?.call_details?.campaign_id) return false;
    const voiceSub = callData.voicestream_subscriptions?.find(
      (sub: any) => sub.streamingMediaType === 'VOICE' && sub.createdBy === 'AI Agents Service'
    );
    if (!voiceSub) return true;
    if (voiceSub.filterType === 'NONE') return false;
    return !voiceSub.filterValues?.includes(callData.call_details.campaign_id);
  }, [callData]);

  // Load sectors on component mount
  useEffect(() => {
    loadSectors();
  }, []);

  const loadSectors = async () => {
    try {
      const data: SectorData = await apiService.getSectors();

      const sortedSectors = [...data.sectors].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

      setAvailableSectors(sortedSectors);
      setSectorMapping(data.sector_project_mapping);
      setGkeMapping(data.sector_gke_mapping);
      
      // // Auto-select first sector if available
      // if (sortedSectors.length > 0) {
      //   setSector(sortedSectors[0]);
      // }
    } catch (err) {
      console.error('Failed to load sectors:', err);
      setError('Failed to load sectors');
    }
  };

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!callId.trim() || !domainId.trim() || !sector.trim()) {
      setError('Please provide Call ID, Domain ID, and select a Sector');
      return;
    }

    window.localStorage.setItem('lastCallId', callId);
    window.localStorage.setItem('lastDomainId', domainId);
    window.localStorage.setItem('lastSector', sector);

    setLoading(true);
    setError('');
    setCallData(null);
    setAgent({});
    setAgents({})
    setAgentIds([]);
    setAgentId('');

    try {
      const data = await apiService.troubleshoot(callId, domainId, sector);
      setCallData(data);
      if(data?.call_details?.agents_details) {
        let amap = data.call_details.agents_details
        setAgents(amap)
        let ids = amap ? loadAgentIds(amap) : []
        setAgentIds(ids)
        let id = ids[0]
        setAgentId(id)
        setAgent(amap[id])
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch call data');
    } finally {
      setLoading(false);
    }
  };

  const loadAgentIds = (agents_details: Record<string, Record<string, string>>[]) => {
    return Object.keys(agents_details)
  }

  const handleEventClick = (timestamp: number) => {
    setCurrentTime(timestamp);
  };

  const handleTimeChange = (timestamp: number) => {
    setCurrentTime(timestamp);
  };

  const handleOpenVAConfig = async () => {
    if (!domainId || !sector) {
      setError('Please provide domain ID and sector to view virtual assistant configuration');
      return;
    }

    setShowVAConfig(true);
    setVAConfigLoading(true);
    
    try {
      // Use the current call data if available, otherwise make a request to get VA config
      if (callData && callData.virtual_agent_config) {
        setVAConfigData(callData.virtual_agent_config);
      } else {
        // Make a separate request to get VA config
        const response = await apiService.troubleshoot('config-only', domainId, sector);
        setVAConfigData(response.virtual_agent_config || []);
      }
    } catch (err) {
      console.error('Failed to load virtual assistant configuration:', err);
      setError('Failed to load virtual assistant configuration');
    } finally {
      setVAConfigLoading(false);
    }
  };

  const handleCloseVAConfig = () => {
    setShowVAConfig(false);
    setVAConfigData(null);
  };

  const handleOpenESSConfig = async () => {
    if (!domainId || !sector) {
      setError('Please provide domain ID and sector to view ESS configuration');
      return;
    }

    setShowESSConfig(true);
    setESSConfigLoading(true);

    try {
      // Use the current call data if available, otherwise make a request to get ESS config
      if (callData && callData.ess_subscriptions) {
        setESSConfigData(callData.ess_subscriptions.filter((sub: any) => sub.userId === 'ai-agents' || sub.userId === 'voicestream'));
      } else {
        // Make a separate request to get ESS config
        const response = await apiService.troubleshoot('config-only', domainId, sector);
        setESSConfigData((response.ess_subscriptions || []).filter((sub: any) => sub.userId === 'ai-agents' || sub.userId === 'voicestream'));
      }
    } catch (err) {
      console.error('Failed to load ESS configuration:', err);
      setError('Failed to load ESS configuration');
    } finally {
      setESSConfigLoading(false);
    }
  }

  const handleCloseESSConfig = () => {
    setShowESSConfig(false);
    setESSConfigData(null);
  };

  const handleOpenFlowAnalyzer = () => {
    setShowFlowAnalyzer(true);
  };

  const handleCloseFlowAnalyzer = () => {
    setShowFlowAnalyzer(false);
  };

    const handleOpenVoiceStreamAnalyzer = () => {
    setShowVoiceStreamAnalyzer(true);
  };

  const handleCloseVoiceStreamAnalyzer = () => {
    setShowVoiceStreamAnalyzer(false);
  };

  const handleOnTranscriptAnalyzer = () => {
    setShowTranscriptionAnalyzer(true);
  };

  const handleCloseTranscriptionAnalyzer = () => {
    setShowTranscriptionAnalyzer(false);
  };

  const handleOpenTagAnalyser = () => {
    setShowTagsAnalyzer(true);
  };

  const handleCloseTagsAnalyzer = () => {
    setShowTagsAnalyzer(false);
  };

  const handleOpenSLI = () => {
    setShowSLI(true);
  };

  const handleCloseSLI = () => {
    setShowSLI(false);
  };

  return (
    <div className="app">
      <Header 
        userEmail={userEmail} 
        onLogout={onLogout}
        onVAConfigClick={handleOpenVAConfig}
        onESSConfigClick={handleOpenESSConfig}
        onFlowAnalyzerClick={handleOpenFlowAnalyzer}
        onTagAnalyzerClick={handleOpenTagAnalyser}
        onTranscriptionAnalyzerClick={handleOnTranscriptAnalyzer}
        onVoiceStreamAnalyzerClick={handleOpenVoiceStreamAnalyzer}
        onSLIClick={handleOpenSLI}
        callId={callData?.call_details?.interaction_id}
        domainId={domainId}
        sessionId={callData?.call_details?.sessionId}
        sector={sector}
      />
      
      <div style={{ padding: '2rem' }}>
        {/* Search Form */}
        <div style={{ 
          backgroundColor: 'var(--five9-white)', 
          borderRadius: '8px', 
          padding: '1.5rem', 
          marginBottom: '1.5rem',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600', 
            marginBottom: '1rem',
            color: 'var(--five9-dark-gray)'
          }}>
            Search Call Data
          </h2>
          <form onSubmit={handleSearch}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '1rem' 
            }}>
              <div>
                <label className="form-label">Call ID</label>
                <input
                  type="text"
                  value={callId}
                  onChange={(e) => setCallId(e.target.value)}
                  className="form-input"
                  placeholder="Enter call ID"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="form-label">Domain ID</label>
                <input
                  type="text"
                  value={domainId}
                  onChange={(e) => setDomainId(e.target.value)}
                  className="form-input"
                  placeholder="Enter domain ID"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="form-label">Sector</label>
                <select
                  value={sector ? sector : (availableSectors.length > 0 ? availableSectors[0] : '')}
                  onChange={(e) => setSector(e.target.value)}
                  className="form-input"
                  disabled={loading}
                >
                  <option value="">Select a sector</option>
                  {availableSectors.map((sectorName) => (
                    <option key={sectorName} value={sectorName}>
                      {sectorName} (Project: {sectorMapping[sectorName] || 'Unknown'})
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'end' }}>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                  style={{ width: '100%' }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
          </form>
          
          {error && (
            <div className="error" style={{ marginTop: '1rem' }}>
              {error}
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{
            backgroundColor: 'var(--five9-white)',
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}>
            <div className="loading">Loading call data...</div>
          </div>
        )}

        {/* Main Content */}
        {callData && (
          <div className="main-content" style={{ 
            height: 'calc(100vh - 300px)',
            border: '1px solid var(--five9-light-gray)',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}>
            <div className="events-timeline">
              <div className="timeline-header">Events Timeline</div>
              <div className="timeline-content">
                <EventsTimeline
                  events={callData.interaction_events || []}
                  currentTime={currentTime}
                  onEventClick={handleEventClick}
                />
              </div>
            </div>
            
            <div className="transcript-panel">
              <div className="transcript-header">Transcript</div>
              <div className="transcript-content">
                <TranscriptPanel
                  transcripts={callData.transcript_data || []}
                  missingVSSub={missingVSSub}
                  currentTime={currentTime}
                  onTimeChange={handleTimeChange}
                />
              </div>
            </div>

            <div className="call-details-panel">
              <div className="call-details-header">Call Details</div>
              <div className="call-details-content">
              {agentIds.length>0 && <div>
                <h4 className="agent-item">
                    <label className='agent-label'>Agent ID: </label>
                    <select
                    value={agentId}
                    onChange={(e) => {
                      setAgentId(e.target.value)
                      setAgent(agents[e.target.value])}}
                    className="agent-select"
                    disabled={loading}
                    >
                    <option value="">Select an agentId</option>
                    {agentIds.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </h4>
              </div>}
                {callData.call_details && agent ? (
                  <div>
                    {/* Basic Call Information */}
                    <div className="call-basic-info">
                      <div className="info-item">
                        <strong>Agent Name:</strong> {agent['agent_name'] || 'N/A'}
                      </div>
                      <div className="info-item">
                        <strong>AA Access:</strong> {agent['user_has_access'] ? 'Yes' : 'No'}
                      </div>
                      <div className="info-item">
                        <strong>Session ID:</strong> {callData.call_details.session_id || 'N/A'}
                      </div>
                      <div className="info-item">
                        <strong>Assistant ID:</strong> {agent['assistant_id'] || 'N/A'}
                      </div>
                      <div className="info-item">
                        <strong>Assistant Type:</strong> {agent['assistant_type'] || 'N/A'}
                      </div>
                      <div className="info-item">
                        <strong>Assistant Name:</strong> {agent['assistant_name'] || 'N/A'}
                      </div>
                      <div className="info-item">
                        <strong>Campaign ID:</strong> {agent['campaign_id'] || 'N/A'}
                      </div>
                      <div className="info-item">
                        <strong>Campaign Name:</strong> {agent['campaign_name'] || 'N/A'}
                      </div>
                      <div className="info-item">
                        <strong>Skill ID:</strong> {agent['skill_id'] || 'N/A'}
                      </div>
                      <div className="info-item">
                        <strong>Skill Name:</strong> {agent['skill_name'] || 'N/A'}
                      </div>
                      <div className="info-item">
                        <strong>Call Start:</strong> {agent['created_at'] ?
                          new Date(agent['created_at']).toLocaleString() : 'N/A'}
                      </div>
                      <div className="info-item">
                        <strong>Call End:</strong> {agent['ended_at'] ?
                          new Date(agent['ended_at']).toLocaleString() : 'N/A'}
                      </div>
                    </div>

                    {/* CAV Variables Table */}
                    {agent['variables'] && Object.keys(agent['variables']).length > 0 && (
                      <div className="cav-section">
                        <h4>CAV Variables</h4>
                        <div className="cav-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Variable Name</th>
                                <th>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(agent['variables']).map(([key, value]) => (
                                <tr key={key}>
                                  <td>{key}</td>
                                  <td>{String(value)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Features Enabled */}
                    <div className="features-section">
                      <h4>Features Enabled</h4>
                      <div className="features-grid">
                        <div className="feature-item">
                          <span className={agent['guidance'] ? 'feature-enabled' : 'feature-disabled'}>
                            Guidance: {agent['guidance'] ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="feature-item">
                          <span className={agent['enable_knowledge_dist'] ? 'feature-enabled' : 'feature-disabled'}>
                            Knowledge: {agent['enable_knowledge_dist'] ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="feature-item">
                          <span className={agent['nlp'] ? 'feature-enabled' : 'feature-disabled'}>
                            NLP: {agent['nlp'] ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="feature-item">
                          <span className={agent['summary'] ? 'feature-enabled' : 'feature-disabled'}>
                            Summary: {agent['summary'] ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="feature-item">
                          <span className={agent['summary_v2'] ? 'feature-enabled' : 'feature-disabled'}>
                            Summary V2: {agent['summary_v2'] ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="feature-item">
                          <span className={agent['transcripts_enabled'] ? 'feature-enabled' : 'feature-disabled'}>
                            Transcripts: {agent['transcripts_enabled'] ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-call-details">
                    No call details available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Virtual Assistant Configuration Modal */}
        {showVAConfig && (
          <VirtualAssistantConfig
            onClose={handleCloseVAConfig}
            loading={vaConfigLoading}
            configData={vaConfigData}
          />
        )}

        {/* ESS Configuration Modal */}
        {showESSConfig && (
          <ESSConfig
            onClose={handleCloseESSConfig}
            loading={essConfigLoading}
            configData={essConfigData}
          />
        )}

        {/* Flow Service Analyzer Modal */}
        {showFlowAnalyzer && callData?.call_details?.interaction_id && domainId && gkeMapping[sector] && (() => {
          const acceptedEvent = callData?.call_analysis?.timeline?.find((event: any) => event.event_type === 'Accepted');
          const dispositionedEvent = callData?.call_analysis?.timeline?.find((event: any) => event.event_type === 'Dispositioned');

          const callStartTimestamp = acceptedEvent?.timestamp || (callData.call_details['created_at'] ? new Date(callData.call_details['created_at']).toISOString() : undefined);
          const callEndTimestamp = dispositionedEvent?.timestamp || (callData.call_details['ended_at'] ? new Date(callData.call_details['ended_at']).toISOString() : undefined);

          return (
            <FlowServiceAnalyzer
              callId={callData.call_details.interaction_id}
              domainId={domainId}
              projectId={gkeMapping[sector]}
              callStartTimestamp={callStartTimestamp}
              callEndTimestamp={callEndTimestamp}
              onClose={handleCloseFlowAnalyzer}
            />
          );
        })()}

       {/* Transcription Service Analyzer Modal */}
        {showTranscriptionAnalyzer && callData?.call_details?.interaction_id && domainId && gkeMapping[sector] && (() => {
          const acceptedEvent = callData?.call_analysis?.timeline?.find((e: any) => e.event_type === 'Accepted');
          const dispositionedEvent = callData?.call_analysis?.timeline?.find((e: any) => e.event_type === 'Dispositioned');

          const callStartTimestamp = acceptedEvent?.timestamp || (callData.call_details['created_at'] ? new Date(callData.call_details['created_at']).toISOString() : undefined);
          const callEndTimestamp = dispositionedEvent?.timestamp || (callData.call_details['ended_at'] ? new Date(callData.call_details['ended_at']).toISOString() : undefined);

          return (
            <TranscriptionServiceAnalyzer
              callId={callData.call_details.interaction_id}
              domainId={domainId}
              projectId={gkeMapping[sector]}
              callStartTimestamp={callStartTimestamp}
              callEndTimestamp={callEndTimestamp}
              onClose={handleCloseTranscriptionAnalyzer}
            />
          );
        })()}

        {/* Tags Service Analyzer Modal */}
        {showTagsAnalyzer && callData?.call_details?.interaction_id && domainId && gkeMapping[sector] && (() => {
          const acceptedEvent = callData?.call_analysis?.timeline?.find((event: any) => event.event_type === 'Accepted');
          const dispositionedEvent = callData?.call_analysis?.timeline?.find((event: any) => event.event_type === 'Dispositioned');

          const callStartTimestamp = acceptedEvent?.timestamp || (callData.call_details['created_at'] ? new Date(callData.call_details['created_at']).toISOString() : undefined);
          const callEndTimestamp = dispositionedEvent?.timestamp || (callData.call_details['ended_at'] ? new Date(callData.call_details['ended_at']).toISOString() : undefined);

          return (
            <TagsServiceAnalyzer
              callId={callData.call_details.interaction_id}
              domainId={domainId}
              projectId={gkeMapping[sector]}
              callStartTimestamp={callStartTimestamp}
              callEndTimestamp={callEndTimestamp}
              onClose={handleCloseTagsAnalyzer}
            />
          );
        })()}

        {/* Voice Stream Analyzer Modal */}
        {showVoiceStreamAnalyzer && callData?.call_details?.interaction_id && domainId && gkeMapping[sector] && (() => {
          const acceptedEvent = callData?.call_analysis?.timeline?.find((e: any) => e.event_type === 'Accepted');
          const dispositionedEvent = callData?.call_analysis?.timeline?.find((e: any) => e.event_type === 'Dispositioned');

          const callStartTimestamp = acceptedEvent?.timestamp || (callData.call_details['created_at'] ? new Date(callData.call_details['created_at']).toISOString() : undefined);
          const callEndTimestamp = dispositionedEvent?.timestamp || (callData.call_details['ended_at'] ? new Date(callData.call_details['ended_at']).toISOString() : undefined);

          return (
            <VoiceStreamAnalyzer
              callId={callData.call_details.interaction_id}
              domainId={domainId}
              projectId={gkeMapping[sector]}
              callStartTimestamp={callStartTimestamp}
              callEndTimestamp={callEndTimestamp}
              onClose={handleCloseVoiceStreamAnalyzer}
            />
          );
        })()}

        {/* SLI Analyzer Modal */}
        {showSLI && callData?.sli_metrics && (() => {
          return (
            <SLIAnalyzer
              onClose={handleCloseSLI}
              sli_metrics={callData.sli_metrics}
            />
          );
        })()}


      </div>
    </div>
  );
};

export default TranscriptViewer;
